<template>
  <AiEducationLayout>
    <div class="ai-edu-admin-page">
      <ContentWrap title="课表排课">
        <el-form :inline="true" :model="query" class="-mb-15px">
          <el-form-item label="学校">
            <el-select
              v-model="query.schoolId"
              class="!w-200px"
              clearable
              filterable
              @change="onSchoolChange"
            >
              <el-option v-for="s in schools" :key="s.id" :label="s.name" :value="s.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="学期">
            <el-select
              v-model="query.termId"
              class="!w-180px"
              clearable
              filterable
              @change="handleQuery"
            >
              <el-option v-for="t in terms" :key="t.id" :label="t.name" :value="t.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="教学班">
            <el-select
              v-model="query.teachingClassId"
              class="!w-200px"
              clearable
              filterable
              @change="handleQuery"
            >
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="handleQuery"><Icon icon="ep:search" />搜索</el-button>
            <el-button
              v-hasPermi="['ai-education:timetable:write']"
              plain
              type="primary"
              @click="openDialog()"
            >
              <Icon icon="ep:plus" />新增排课
            </el-button>
          </el-form-item>
        </el-form>
      </ContentWrap>
      <ContentWrap>
        <el-table v-loading="loading" :data="list" border>
          <el-table-column prop="teachingClassName" label="教学班" min-width="140" />
          <el-table-column label="星期" width="90">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_WEEKDAY" :value="row.weekday" />
            </template>
          </el-table-column>
          <el-table-column label="节次" width="100">
            <template #default="{ row }">{{ row.periodStart }}-{{ row.periodEnd }}</template>
          </el-table-column>
          <el-table-column label="周次" width="100">
            <template #default="{ row }">{{ row.weekStart }}-{{ row.weekEnd }}</template>
          </el-table-column>
          <el-table-column prop="roomText" label="教室" min-width="120" />
          <el-table-column label="状态" width="90">
            <template #default="{ row }">
              <dict-tag :type="DICT_TYPE.AI_EDU_COMMON_STATUS" :value="row.status" />
            </template>
          </el-table-column>
          <el-table-column prop="createTime" label="创建时间" width="170" />
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="{ row }">
              <el-button
                v-hasPermi="['ai-education:timetable:write']"
                link
                type="primary"
                @click="openDialog(row)"
                >编辑</el-button
              >
              <el-button
                v-hasPermi="['ai-education:timetable:delete']"
                link
                type="danger"
                @click="handleDelete(row.id)"
                >删除</el-button
              >
            </template>
          </el-table-column>
        </el-table>
      </ContentWrap>

      <Dialog v-model="dialogVisible" :title="form.id ? '编辑排课' : '新增排课'" width="520px">
        <el-form ref="formRef" :model="form" :rules="rules" label-width="100px">
          <el-form-item label="教学班" prop="teachingClassId">
            <el-select v-model="form.teachingClassId" class="w-100%" filterable>
              <el-option v-for="c in teachingClasses" :key="c.id" :label="c.name" :value="c.id!" />
            </el-select>
          </el-form-item>
          <el-form-item label="星期" prop="weekday">
            <el-select v-model="form.weekday" class="w-100%">
              <el-option
                v-for="d in getIntDictOptions(DICT_TYPE.AI_EDU_WEEKDAY)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="开始节次" prop="periodStart">
            <el-input-number v-model="form.periodStart" :min="1" :max="20" />
          </el-form-item>
          <el-form-item label="结束节次" prop="periodEnd">
            <el-input-number v-model="form.periodEnd" :min="1" :max="20" />
          </el-form-item>
          <el-form-item label="起始周">
            <el-input-number v-model="form.weekStart" :min="1" :max="30" />
          </el-form-item>
          <el-form-item label="结束周">
            <el-input-number v-model="form.weekEnd" :min="1" :max="30" />
          </el-form-item>
          <el-form-item label="教室"><el-input v-model="form.roomText" /></el-form-item>
          <el-form-item label="状态">
            <el-select v-model="form.status" class="w-100%">
              <el-option
                v-for="d in getIntDictOptions(DICT_TYPE.AI_EDU_COMMON_STATUS)"
                :key="d.value"
                :label="d.label"
                :value="d.value"
              />
            </el-select>
          </el-form-item>
          <el-alert
            v-if="conflicts.length"
            :title="`检测到 ${conflicts.length} 条冲突`"
            type="warning"
            :closable="false"
            class="mb-12px"
          />
        </el-form>
        <template #footer>
          <el-button @click="checkConflict">冲突检测</el-button>
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button :loading="saving" type="primary" @click="submit">保存</el-button>
        </template>
      </Dialog>
    </div>
  </AiEducationLayout>
</template>

<script lang="ts" setup>
import * as AiEducationApi from '@/api/aiEducation'
import * as UniversityApi from '@/api/aiEducation/university'
import AiEducationLayout from '../../layout/AiEducationLayout.vue'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'AiEduTimetable' })

const message = useMessage()
const loading = ref(false)
const saving = ref(false)
const list = ref<UniversityApi.TimetableVO[]>([])
const conflicts = ref<UniversityApi.TimetableVO[]>([])
const schools = ref<AiEducationApi.SchoolVO[]>([])
const terms = ref<UniversityApi.TermSimpleVO[]>([])
const teachingClasses = ref<AiEducationApi.TeachingClassVO[]>([])
const query = reactive<{ schoolId?: number; termId?: number; teachingClassId?: number }>({})
const dialogVisible = ref(false)
const formRef = ref<FormInstance>()
const form = reactive<Partial<UniversityApi.TimetableVO>>({})
const rules: FormRules = {
  teachingClassId: [{ required: true, message: '请选择教学班', trigger: 'change' }],
  weekday: [{ required: true, message: '请选择星期', trigger: 'change' }],
  periodStart: [{ required: true, message: '请填写开始节次', trigger: 'change' }],
  periodEnd: [{ required: true, message: '请填写结束节次', trigger: 'change' }]
}

const onSchoolChange = async () => {
  query.termId = undefined
  query.teachingClassId = undefined
  terms.value = query.schoolId
    ? (await UniversityApi.getTermSimpleList(query.schoolId)) || []
    : []
  teachingClasses.value = query.schoolId
    ? (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
    : []
  await load()
}
const load = async () => {
  loading.value = true
  try {
    list.value =
      (await UniversityApi.getTimetableList({
        teachingClassId: query.teachingClassId,
        termId: query.termId
      })) || []
  } finally {
    loading.value = false
  }
}
const handleQuery = () => load()
const openDialog = async (row?: UniversityApi.TimetableVO) => {
  conflicts.value = []
  if (!teachingClasses.value.length && query.schoolId) {
    teachingClasses.value =
      (await AiEducationApi.getTeachingClassList({ schoolId: query.schoolId })) || []
  }
  Object.assign(form, {
    id: row?.id,
    teachingClassId: row?.teachingClassId ?? query.teachingClassId,
    weekday: row?.weekday ?? 1,
    periodStart: row?.periodStart ?? 1,
    periodEnd: row?.periodEnd ?? 2,
    weekStart: row?.weekStart ?? 1,
    weekEnd: row?.weekEnd ?? 18,
    roomText: row?.roomText || '',
    status: row?.status ?? 20
  })
  dialogVisible.value = true
}
const checkConflict = async () => {
  if (!form.teachingClassId || !form.weekday || !form.periodStart || !form.periodEnd) {
    message.warning('请先填写教学班、星期与节次')
    return
  }
  conflicts.value =
    (await UniversityApi.conflictCheckTimetable({
      id: form.id,
      teachingClassId: form.teachingClassId,
      weekday: form.weekday,
      periodStart: form.periodStart,
      periodEnd: form.periodEnd,
      weekStart: form.weekStart,
      weekEnd: form.weekEnd
    })) || []
  if (!conflicts.value.length) message.success('无冲突')
}
const submit = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    await UniversityApi.saveTimetable(form)
    message.success('保存成功')
    dialogVisible.value = false
    await load()
  } finally {
    saving.value = false
  }
}
const handleDelete = async (id?: number) => {
  if (!id) return
  await message.delConfirm()
  await UniversityApi.deleteTimetable(id)
  message.success('删除成功')
  await load()
}

onMounted(async () => {
  schools.value = (await AiEducationApi.getSchoolList()) || []
  await load()
})
</script>
